// UPLOAD, the write half of an Attached files panel (spec 09 §4.2's E2-2a2
// annotation, §4.4 and §4.7's attachment parts; §2.29, §5's E2-2 row 10;
// D-323 rulings 8 to 11, D-335 rulings 1 and 2).
//
// FOUR CALLS, ONE PRESS. The registry's three steps and then the attach:
// `POST /v1/files` declares the type, the size and the SHA-256 and answers a
// presigned PUT; the PUT sends the bytes straight to the object store under
// the type the create answered (an `image/vnd.dwg` declaration comes back as
// `application/acad`, and the PUT must carry the row's type or its signature
// does not match); `POST /v1/files/{id}/store` checks the bytes against the
// declaration and refuses a file that says it is something else with
// `FILE_INTEGRITY_MISMATCH`; `POST /v1/sales-attachments` writes the link.
//
// A SECOND PRESS RESUMES, IT DOES NOT START OVER. Each step's result is held
// for the file it was made for, so a press after a lost response or a network
// cut picks up at the step that failed: a stored file is attached, never
// uploaded twice, and a created row is PUT to again rather than leaving a
// second pending row behind. Each write keeps its Idempotency-Key until the
// server has answered it (`answeredRefusal`), so a resend after a lost
// response is replayed from the server's store rather than written twice. A
// refusal the server answered retires the step: a refused create mints a
// fresh key, a refused PUT (an expired presign) or a refused store (the byte
// check) drops the held file so the next press starts from the create, and
// the attach keeps its attempt key by `lib/attempt-key.ts`'s rule. Choosing
// another file drops everything held, and changing the category or the title
// starts a new attach attempt over the same stored file, because the old key
// under a new body answers 409.
//
// WHAT THE READER IS TOLD. The browser refuses before a byte is read what the
// server would refuse at the create (a type the purpose does not store, an
// archive among them, an empty file, one over 25 MB), with the sentence the
// server's refusal would earn (`uploadProblem`). Progress reads as a phase
// and, while the bytes go up, a percentage over a native `<progress>`. A
// refusal reads inline under the form in the reader's words, the byte check
// included ("This file's contents are not what its name says"), and the
// server's own title beside it. Offline, Upload is disabled and one line says
// why; an upload cannot be queued, because its presign expires.
//
// THE CATEGORY starts on `commercial`, and on `technical` for a drawing, each
// time a file is chosen, and the uploader may change it. A reader without
// `sales.read_value` may still attach a `commercial` file (the attach checks
// the subject's write code alone), and the form says before the press that
// they will not see it afterwards (§7 narrowing 19).

import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Field } from "../../../../../components/field";
import { Select } from "../../../../../components/select";
import { RequestError, newIdempotencyKey } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useAttemptKey } from "../../lib/attempt-key";
import { useReadsHeldOffline } from "../../lib/reads";
import { salesKeys } from "../../hooks/sales-queries";
import { canReadCommercial, useSalesPermissions } from "../../hooks/permissions";
import {
  UploadTransferError,
  answeredRefusal,
  attachmentKeys,
  realAttachmentUploadWire,
  type AttachmentSubject,
  type AttachmentUploadWire,
  type CreatedUpload,
} from "../../hooks/attachment-queries";
import {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_TITLE_MAX,
  attachmentAccept,
  attachmentRefusalKey,
  declaredType,
  defaultCategory,
  formatSize,
  mergedWinnerOf,
  sha256Hex,
  titleFits,
  titleForSend,
  typeLabel,
  uploadProblem,
  storedTypeOf,
  type AttachmentCategory,
} from "./attachment-model";

type Phase = "idle" | "hashing" | "creating" | "uploading" | "storing" | "attaching";

/** What a press has done so far for the chosen file, so the next press
 * resumes at the step that failed. */
interface Held {
  readonly file: File;
  readonly createKey: string;
  readonly storeKey: string;
  readonly sha256?: string | undefined;
  readonly created?: CreatedUpload | undefined;
  readonly uploaded: boolean;
  readonly stored: boolean;
}

function heldFor(file: File): Held {
  return {
    file,
    createKey: newIdempotencyKey(),
    storeKey: newIdempotencyKey(),
    uploaded: false,
    stored: false,
  };
}

/** The attach refusals after which the stored file can never be linked here,
 * so the next press must upload the file again. */
const FILE_SPENT: ReadonlySet<string> = new Set([
  "ATTACHMENT_ALREADY_LINKED",
  "ATTACHMENT_FILE_WRONG_PURPOSE",
  "ATTACHMENT_FILE_NOT_YOURS",
  "FILE_NOT_STORED",
  "NOT_FOUND",
]);

export function UploadForm({
  subject,
  mergedWinnerName,
  wire,
}: {
  readonly subject: AttachmentSubject;
  /** The winner's name, when the subject is a customer the screen knows was
   * merged, for the merged refusal's sentence. */
  readonly mergedWinnerName?: string | undefined;
  readonly wire?: AttachmentUploadWire | undefined;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const perms = useSalesPermissions();
  const offline = useReadsHeldOffline();
  const attachKey = useAttemptKey();
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const calls = useRef<AttachmentUploadWire>(wire ?? realAttachmentUploadWire());

  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<AttachmentCategory>("commercial");
  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [sent, setSent] = useState(0);
  const [failure, setFailure] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const held = useRef<Held | null>(null);

  const busy = phase !== "idle";
  const problem = file === null ? undefined : uploadProblem(file);
  const fits = titleFits(title);
  const sendable = file !== null && problem === undefined && fits && !offline && !busy;
  const percent =
    file !== null && file.size > 0 ? Math.min(100, Math.round((sent / file.size) * 100)) : 0;
  const hiddenAfter = category === "commercial" && !canReadCommercial(perms);

  function choose(next: File | null) {
    setFailure(null);
    setDone(false);
    setSent(0);
    attachKey.reset();
    held.current = next === null ? null : heldFor(next);
    setFile(next);
    if (next !== null) setCategory(defaultCategory(next));
  }

  async function press() {
    if (file === null || !sendable) return;
    setFailure(null);
    setDone(false);
    let state = held.current?.file === file ? held.current : heldFor(file);
    held.current = state;
    const controller = new AbortController();
    abort.current = controller;
    try {
      if (!state.stored) {
        if (state.created === undefined) {
          let sha = state.sha256;
          if (sha === undefined) {
            setPhase("hashing");
            sha = await sha256Hex(file);
            state = { ...state, sha256: sha };
            held.current = state;
          }
          setPhase("creating");
          try {
            const created = await calls.current.createFile(
              {
                contentType: declaredType(file),
                byteSize: file.size,
                sha256: sha,
                fileName: file.name,
              },
              state.createKey,
            );
            state = { ...state, created, uploaded: false };
            held.current = state;
          } catch (error) {
            if (answeredRefusal(error)) {
              held.current = { ...state, createKey: newIdempotencyKey() };
            }
            throw error;
          }
        }
        const created = state.created;
        if (created === undefined) return;
        if (!state.uploaded) {
          setPhase("uploading");
          setSent(0);
          try {
            await calls.current.putBytes(
              created,
              file,
              (bytes) => {
                setSent(bytes);
              },
              controller.signal,
            );
          } catch (error) {
            // A refused PUT is an expired or spoiled presign: the next press
            // creates a fresh row. A transport loss PUTs to the same row again.
            if (error instanceof UploadTransferError && error.status !== undefined) {
              held.current = heldFor(file);
            }
            throw error;
          }
          state = { ...state, uploaded: true };
          held.current = state;
        }
        setPhase("storing");
        try {
          await calls.current.storeFile(created.fileId, state.storeKey);
        } catch (error) {
          // The byte check (or any other answered refusal) spends the file:
          // the registry row is rejected, and the next press starts over.
          if (answeredRefusal(error)) held.current = null;
          throw error;
        }
        state = { ...state, stored: true };
        held.current = state;
      }
      const stored = state.created;
      if (stored === undefined) return;
      setPhase("attaching");
      try {
        await calls.current.attach(
          {
            fileId: stored.fileId,
            subject,
            category,
            title: titleForSend(title),
          },
          attachKey.take(),
        );
      } catch (error) {
        attachKey.failed(error);
        if (
          error instanceof RequestError &&
          error.code !== undefined &&
          FILE_SPENT.has(error.code)
        ) {
          held.current = null;
        }
        if (mergedWinnerOf(error) !== undefined && subject.kind === "customer") {
          // The customer was merged after this screen read it: read it again,
          // so the 360 becomes the tombstone that names the winner.
          void qc.invalidateQueries({ queryKey: salesKeys.customer(subject.id) });
        }
        throw error;
      }
      attachKey.reset();
      held.current = null;
      setFile(null);
      setTitle("");
      setSent(0);
      setDone(true);
      if (input.current !== null) input.current.value = "";
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) setFailure(error);
    } finally {
      abort.current = null;
      setPhase("idle");
      // Whatever happened, the list is read again: a lost attach may have
      // landed, and a refused one may name a file someone else just linked.
      void qc.invalidateQueries({ queryKey: attachmentKeys.subject(subject) });
    }
  }

  const refusalKey = attachmentRefusalKey(failure);
  const failureText =
    failure instanceof UploadTransferError
      ? t("sales.attachments.upload.transferFailed")
      : refusalKey !== undefined
        ? t(refusalKey, { name: mergedWinnerName ?? t("sales.merge.anotherCustomer") })
        : t("sales.attachments.upload.failed");

  return (
    <div className="sales-attach-upload" data-testid="sales-attach-upload">
      <div className="sales-attach-row">
        <input
          ref={input}
          type="file"
          className="visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          accept={attachmentAccept()}
          data-testid="sales-attach-input"
          disabled={busy}
          onChange={(event) => {
            choose(event.target.files?.[0] ?? null);
          }}
        />
        <Button
          size="sm"
          data-testid="sales-attach-choose"
          disabled={busy}
          onClick={() => {
            input.current?.click();
          }}
        >
          {file === null
            ? t("sales.attachments.upload.choose")
            : t("sales.attachments.upload.chooseOther")}
        </Button>
        {file !== null ? (
          <span
            className="sales-attach-file"
            data-testid="sales-attach-file"
            data-declared={declaredType(file)}
          >
            <strong>{file.name}</strong>{" "}
            <span className="sales-hint num">
              {storedTypeOf(declaredType(file)) !== undefined
                ? `${typeLabel(storedTypeOf(declaredType(file)) ?? "")} · `
                : ""}
              {formatSize(file.size, i18n.language)}
            </span>
          </span>
        ) : (
          <span className="sales-hint" data-testid="sales-attach-types">
            {t("sales.attachments.upload.types", {
              max: formatSize(ATTACHMENT_MAX_BYTES, i18n.language),
            })}
          </span>
        )}
      </div>

      {problem !== undefined ? (
        <p
          className="sales-error-note"
          role="alert"
          data-testid="sales-attach-problem"
          data-problem={problem}
        >
          {t(`sales.attachments.problem.${problem}`, {
            max: formatSize(ATTACHMENT_MAX_BYTES, i18n.language),
          })}
        </p>
      ) : null}

      {file !== null && problem === undefined ? (
        <div className="sales-attach-row">
          <Field label={t("sales.attachments.upload.category")}>
            <Select
              value={category}
              disabled={busy}
              data-testid="sales-attach-category"
              onChange={(event) => {
                attachKey.reset();
                setCategory(event.target.value as AttachmentCategory);
              }}
            >
              <option value="commercial">{t("sales.attachments.category.commercial")}</option>
              <option value="technical">{t("sales.attachments.category.technical")}</option>
            </Select>
          </Field>
          <Field label={t("sales.attachments.upload.title")} wide>
            <input
              type="text"
              value={title}
              disabled={busy}
              maxLength={1000}
              placeholder={t("sales.attachments.upload.titleHint")}
              data-testid="sales-attach-title"
              onChange={(event) => {
                attachKey.reset();
                setTitle(event.target.value.replaceAll("\u0000", ""));
              }}
            />
          </Field>
        </div>
      ) : null}

      {file !== null && problem === undefined ? (
        <p className="sales-hint" data-testid="sales-attach-category-hint">
          {t(`sales.attachments.categoryHint.${category}`)}
        </p>
      ) : null}
      {file !== null && problem === undefined && hiddenAfter ? (
        <p className="sales-hint" data-testid="sales-attach-hidden-after">
          {t("sales.attachments.upload.hiddenAfter")}
        </p>
      ) : null}
      {!fits ? (
        <p className="sales-error-note" role="alert" data-testid="sales-attach-title-long">
          {t("sales.attachments.upload.titleLong", { max: ATTACHMENT_TITLE_MAX })}
        </p>
      ) : null}

      {file !== null && problem === undefined ? (
        <div className="sales-attach-row">
          <Button
            size="sm"
            data-testid="sales-attach-submit"
            disabled={!sendable}
            busy={busy}
            busyLabel={t("sales.attachments.upload.busy")}
            onClick={() => {
              void press();
            }}
          >
            {t("sales.attachments.upload.submit")}
          </Button>
          {phase === "uploading" ? (
            <Button
              size="sm"
              data-testid="sales-attach-cancel"
              onClick={() => {
                abort.current?.abort();
              }}
            >
              {t("sales.common.cancel")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {busy ? (
        <div
          className="sales-attach-progress"
          data-testid="sales-attach-progress"
          data-phase={phase}
        >
          <p className="sales-hint" role="status">
            {phase === "uploading"
              ? t("sales.attachments.upload.phase.uploading", { percent })
              : t(`sales.attachments.upload.phase.${phase}`)}
          </p>
          {phase === "uploading" ? (
            <progress
              max={100}
              value={percent}
              aria-label={t("sales.attachments.upload.progressLabel")}
            />
          ) : null}
        </div>
      ) : null}

      {offline ? (
        <p className="sales-hint" data-testid="sales-attach-offline">
          {t("sales.attachments.upload.offline")}
        </p>
      ) : null}

      {failure !== null && !busy ? (
        <div
          data-testid="sales-attach-failed"
          data-code={failure instanceof RequestError ? (failure.code ?? "") : "transfer"}
        >
          <MutationFailureNote text={failureText} error={failure} />
        </div>
      ) : null}

      {done && !busy ? (
        <p className="sales-hint" role="status" data-testid="sales-attach-done">
          {t("sales.attachments.upload.done")}
        </p>
      ) : null}
    </div>
  );
}
